import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, NavLink, useLocation } from 'react-router-dom'

import { Button } from '@/design-system/Button'
import { LanguageSwitcher } from '@/design-system/LanguageSwitcher'
import { cn } from '@/lib/cn'
import { SUPPORTED_LOCALES, hasChosenLocale, setLocale, type Locale } from '@/lib/i18n'

import { useAdminMutation, useAdminQuery } from './api'

import { useAdminContext, useAdminSession } from './session'

/**
 * Les sections du back-office, dans l'ordre où on prépare une soirée.
 *
 * L'ordre n'est pas alphabétique : on crée l'édition, puis ce qu'on vend
 * (carte, boissons, billets), puis ce qui se passe (programme, concours,
 * votes), puis ce qu'on raconte, et enfin ce qu'on surveille. Quelqu'un qui
 * prépare l'événement pour la première fois peut descendre la liste.
 *
 * `permission` masque une entrée qu'un compte ne peut pas ouvrir — c'est un
 * confort, jamais une sécurité : chaque endpoint revérifie.
 */
export interface AdminSection {
  to: string
  key: string
  icon: string
  permission: string
}

export const SECTIONS: AdminSection[] = [
  { to: '/admin', key: 'event', icon: '🎭', permission: 'event.event.read' },
  // Le projet en deuxième : c'est l'écran qu'on ouvre le plus souvent
  // pendant les mois de préparation, bien avant la carte ou les billets.
  { to: '/admin/projet', key: 'project', icon: '🗂️', permission: 'project.task.read' },
  { to: '/admin/carte', key: 'food', icon: '🍽️', permission: 'catalog.food_item.read' },
  { to: '/admin/boissons', key: 'drinks', icon: '🍾', permission: 'catalog.drink_item.read' },
  { to: '/admin/billets', key: 'tickets', icon: '🎟️', permission: 'ticketing.ticket_type.read' },
  { to: '/admin/programme', key: 'program', icon: '🕘', permission: 'program.item.read' },
  { to: '/admin/concours', key: 'competition', icon: '👑', permission: 'competition.category.read' },
  { to: '/admin/votes', key: 'voting', icon: '🏆', permission: 'voting.category.read' },
  { to: '/admin/contenus', key: 'content', icon: '📝', permission: 'event.content.read' },
  { to: '/admin/commandes', key: 'orders', icon: '🧾', permission: 'orders.order.read' },
  // Juste sous les commandes, même droit de lecture : la même vente, lue
  // côté personnes — qui a réservé quoi, et comment le remercier ensuite.
  {
    to: '/admin/reservations',
    key: 'reservations',
    icon: '📇',
    permission: 'orders.order.read',
  },
  // Juste après les commandes : une demande de table est une vente qui
  // n'a pas encore de prix, et quelqu'un attend au bout.
  {
    to: '/admin/demandes-tables',
    key: 'tableRequests',
    icon: '🍾',
    permission: 'tables.reservation.read',
  },
  // Le placement : quelle table pour chaque réservation payée, qui est
  // arrivé, qui est installé. On le prépare ici ; le soir J, il vit dans
  // l'écran Salle de l'hôtesse.
  { to: '/admin/placement', key: 'placement', icon: '🪑', permission: 'tables.placement.read' },
  { to: '/admin/finances', key: 'finance', icon: '💶', permission: 'finance.report.read' },
  // Juste après les finances : on décide ce qu'on achète en regardant ce
  // qu'on peut y mettre.
  { to: '/admin/courses', key: 'shopping', icon: '🛒', permission: 'finance.shopping.read' },
  // Ce qu'on vise, juste après ce qu'on dépense : les deux se lisent
  // ensemble, et un objectif de recettes sans budget en face ne veut rien
  // dire — ni l'inverse.
  { to: '/admin/objectifs', key: 'forecast', icon: '🎯', permission: 'finance.target.read' },
  // La réserve : ce qui reste, et ce que ça vaut.
  { to: '/admin/stocks', key: 'stock', icon: '📦', permission: 'inventory.movement.read' },
  // Le service en salle : qui sert quelles tables, et le bilan de la nuit
  // par serveur, par poste et par caisse.
  { to: '/admin/service', key: 'service', icon: '🤵', permission: 'service.table.assign' },
  // Juste avant l'équipe : c'est la dernière chose qu'on prépare, une fois la
  // salle montée et les tables numérotées.
  { to: '/admin/qr-codes', key: 'qrcodes', icon: '🔳', permission: 'tables.physical_table.read' },
  // En dernier, littéralement : c'est le seul écran qu'on ouvre après la
  // soirée, et le seul depuis lequel on arrête les comptes.
  { to: '/admin/cloture', key: 'closing', icon: '📊', permission: 'analytics.finance.read' },
  { to: '/admin/equipe', key: 'team', icon: '👥', permission: 'admin.user.read' },
  // Composer ses propres rôles : l'API le permettait, aucun écran ne le
  // montrait.
  { to: '/admin/roles', key: 'roles', icon: '🔐', permission: 'admin.role.read' },
  // Les réglages étaient au bas de l'écran « Événement », sans que rien
  // dans le menu ne porte le mot : on ne les trouvait qu'en faisant
  // défiler un formulaire d'édition jusqu'au bout.
  // Les messages vivaient en base sans qu'aucun écran ne les montre : le
  // seul moyen d'en changer un mot était de modifier le code.
  // Le serveur garde les modèles derrière `admin.notification.*` : le menu
  // demandait `event.content.read`, et l'on ouvrait un écran qui ne
  // répondait que des refus.
  { to: '/admin/messages', key: 'messages', icon: '✉️', permission: 'admin.notification.read' },
  // Qui reçoit quoi : les alertes équipe, catégorie par catégorie, et le
  // serveur par lequel elles partent.
  {
    to: '/admin/notifications',
    key: 'notifications',
    icon: '📬',
    permission: 'admin.notification.read',
  },
  // Combien de personnes ont visité le site — sans cookie.
  { to: '/admin/audience', key: 'audience', icon: '📈', permission: 'analytics.dashboard.read' },
  // Ce que les invités ont pensé de la soirée — anonymement. La pastille
  // compte les avis pas encore lus.
  { to: '/admin/avis', key: 'feedback', icon: '💬', permission: 'feedback.response.read' },
  // Le journal vivait en base sans qu'aucun écran ne le montre : une trace
  // que personne ne peut lire ne protège personne.
  { to: '/admin/journal', key: 'audit', icon: '🕵️', permission: 'admin.audit.read' },
  // Les demandes RGPD arrivaient en base et dans une alerte e-mail, sans
  // écran pour les traiter : le délai légal d'un mois courait dans le vide.
  { to: '/admin/rgpd', key: 'gdpr', icon: '🛡️', permission: 'admin.gdpr.read' },
  { to: '/admin/reglages', key: 'settings', icon: '⚙️', permission: 'event.settings.read' },
  // L'exploitation, pas la préparation : remettre le site de test à niveau.
  // Réservé à ADMIN — le geste efface ce que les testeurs y ont fait.
  {
    to: '/admin/systeme-test',
    key: 'testSystem',
    icon: '🧪',
    permission: 'admin.test_env.manage',
  },
  // En dernier, et lisible par tous ceux qui ouvrent le back-office : le
  // manuel ne sert à rien s'il est réservé à qui n'a plus besoin de lire.
  { to: '/admin/documentation', key: 'documentation', icon: '📖', permission: 'event.event.read' },
]

/**
 * Le catalogue est gardé par un **parapluie** : `catalog.manage` autorise tout
 * ce qui commence par `catalog.`. Un compte qui l'a mais n'a pas la permission
 * fine verrait sinon son menu amputé de rubriques qu'il peut ouvrir.
 */
const UMBRELLAS: Record<string, string> = {
  'catalog.food_item.read': 'catalog.manage',
  'catalog.drink_item.read': 'catalog.manage',
  // Le trésorier lit le bilan du service sans attribuer de tables.
  'service.table.assign': 'service.report.read',
}


export function AdminLayout({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation()
  const { session, signOut } = useAdminSession()
  const { event, eventId, editions, selectEvent, can, me } = useAdminContext()
  const [menuOpen, setMenuOpen] = useState(false)

  const saveLocale = useAdminMutation<unknown, { locale: Locale }>(
    'put',
    () => '/auth/me/locale',
    eventId,
    [['admin-me']],
  )

  // La langue du compte ne sert qu'à un appareil où rien n'a encore été
  // choisi. Elle écrasait auparavant, à chaque nouvelle ouverture, la langue
  // choisie sur le téléphone — et comme tout compte naît en allemand,
  // l'interface « revenait toujours en allemand ». Désormais un choix fait
  // sur l'appareil l'emporte, et c'est lui qu'on enregistre sur le compte.
  const accountLocale = me?.user.locale
  const synced = useRef(false)
  useEffect(() => {
    if (synced.current || !accountLocale) return
    synced.current = true
    const current = i18n.language as Locale
    if (hasChosenLocale()) {
      if (accountLocale !== current) saveLocale.mutate({ locale: current })
    } else if ((SUPPORTED_LOCALES as readonly string[]).includes(accountLocale)) {
      if (accountLocale !== current) void setLocale(accountLocale as Locale)
    }
  }, [accountLocale, i18n.language, saveLocale])

  // Un changement d'écran referme le menu et repart du haut : sur téléphone,
  // la nouvelle page s'ouvrait au niveau de défilement de l'ancienne, et l'on
  // croyait ne pas avoir quitté l'écran précédent.
  const { pathname } = useLocation()
  useEffect(() => {
    setMenuOpen(false)
    window.scrollTo(0, 0)
  }, [pathname])

  // Menu ouvert sur téléphone : il couvre l'écran et la page ne défile plus
  // derrière lui.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [menuOpen])

  // Tant que les permissions ne sont pas connues, on montre tout : masquer
  // par défaut donnerait un menu vide au premier rendu, qui se remplirait
  // sous les yeux de l'utilisateur.
  // Un mot de passe tiré par quelqu'un d'autre a circulé par message : tant
  // qu'il n'est pas remplacé, le back-office n'ouvre que « Mon compte ».
  // Confort d'interface, pas une barrière : le serveur reste seul juge des
  // droits, et le changement se fait de toute façon depuis cette page.
  const mustChangePassword = Boolean(me?.user.must_change_password)

  const sections = me
    ? SECTIONS.filter(
        (section) =>
          can(section.permission) ||
          (UMBRELLAS[section.permission] ? can(UMBRELLAS[section.permission]!) : false),
      )
    : SECTIONS

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[16rem_1fr]">
      {/* Sur téléphone, la barre du haut reste visible pendant qu'on fait
          défiler une page : le bouton du menu ne disparaît plus en haut de
          l'écran. Menu ouvert, elle couvre tout l'écran et défile seule — la
          page d'en dessous ne transparaît plus sous la liste des rubriques. */}
      <aside
        className={cn(
          'border-line-subtle bg-bg-elevated lg:sticky lg:top-0 lg:h-dvh lg:border-r',
          'sticky top-0 z-40 flex flex-col',
          menuOpen && 'max-lg:fixed max-lg:inset-0 max-lg:h-dvh max-lg:overflow-y-auto',
        )}
      >
        <div className="flex items-center justify-between gap-3 border-b border-line-subtle px-5 py-3 lg:py-4">
          <div className="flex min-w-0 items-center gap-3">
            <img src="/leyamo.png" alt="" className="h-8 w-8 shrink-0 rounded-full" />
            <div className="min-w-0">
              <p className="truncate text-sm text-content-primary">{event?.name ?? 'LeYAMO'}</p>
              {/* Plusieurs éditions : on choisit celle qu'on prépare (ou dont on
                  fait le bilan), sans dépendre de celle que le site affiche. */}
              {editions.length > 1 ? (
                <select
                  value={eventId ?? ''}
                  onChange={(change) => selectEvent(change.target.value || null)}
                  aria-label={t('admin.edition')}
                  className="mt-0.5 max-w-full rounded border border-line bg-bg-base px-1.5 py-0.5 text-xs text-content-secondary"
                >
                  {editions.map((edition) => (
                    <option key={edition.id} value={edition.id}>
                      {edition.edition_year} · {t(`admin.editionStatus.${edition.status}`)}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-xs text-content-muted">{t('admin.overline')}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-label={menuOpen ? t('nav.close') : t('nav.open')}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-line text-content-primary lg:hidden"
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>

        <nav
          aria-label={t('admin.overline')}
          className={cn('flex-1 overflow-y-auto p-3', menuOpen ? 'block' : 'hidden lg:block')}
        >
          {sections.map((section) => (
            <NavLink
              key={section.to}
              to={section.to}
              // `end` sur la racine : sans lui, « Édition » resterait
              // surlignée sur toutes les sous-pages.
              end={section.to === '/admin'}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                cn(
                  'mb-0.5 flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition duration-[var(--dur-fast)]',
                  isActive
                    ? 'bg-bg-overlay text-content-primary'
                    : 'text-content-secondary hover:bg-bg-overlay/60 hover:text-content-primary',
                )
              }
            >
              <span aria-hidden className="text-base">
                {section.icon}
              </span>
              {t(`admin.section.${section.key}`)}
              {section.key === 'feedback' && me && can('feedback.response.read') ? (
                <UnreadFeedbackBadge eventId={eventId} />
              ) : null}
            </NavLink>
          ))}
        </nav>

        <div
          className={cn(
            'border-t border-line-subtle p-3',
            menuOpen ? 'block' : 'hidden lg:block',
          )}
        >
          {/* Le même sélecteur que partout ailleurs ; ici, le choix est aussi
              enregistré sur le compte (e-mails, prochain appareil). */}
          <div className="mb-2 flex items-center justify-between gap-2 px-3 text-xs text-content-muted">
            <span>{t('admin.language')}</span>
            <LanguageSwitcher onChange={(locale) => saveLocale.mutate({ locale })} />
          </div>
          <p className="truncate px-3 text-xs text-content-muted">{session.user.email}</p>
          {/* Son propre compte : mot de passe et langue. On le cherche là où
              l'on lit son adresse, à côté de « Se déconnecter ». */}
          <NavLink
            to="/admin/compte"
            onClick={() => setMenuOpen(false)}
            className={({ isActive }) =>
              cn(
                'mt-1 flex items-center gap-2 rounded-md px-3 py-2 text-sm transition',
                isActive
                  ? 'bg-bg-overlay text-content-primary'
                  : 'text-content-secondary hover:bg-bg-overlay/60 hover:text-content-primary',
              )
            }
          >
            <span aria-hidden>👤</span>
            {t('admin.section.account')}
            {mustChangePassword ? (
              <span className="ml-auto h-2 w-2 rounded-full bg-state-warning" aria-hidden />
            ) : null}
          </NavLink>
          <Button variant="ghost" size="sm" className="mt-1 w-full" onClick={signOut}>
            {t('admin.signOut')}
          </Button>
        </div>
      </aside>

      <main className="min-w-0 px-5 py-8 sm:px-8">
        {mustChangePassword && pathname !== '/admin/compte' ? (
          <Navigate to="/admin/compte" replace />
        ) : (
          children
        )}
      </main>
    </div>
  )
}

/**
 * Le nombre d'avis d'invités pas encore lus, à côté de « Avis » dans le menu.
 * Relu chaque minute tant que l'onglet est visible ; rien à zéro.
 */
function UnreadFeedbackBadge({ eventId }: { eventId: string | undefined }) {
  const { t } = useTranslation()
  const query = useAdminQuery<{ unread: number }>(
    ['admin-feedback-unread', eventId],
    `/admin/events/${eventId}/feedback/unread`,
    eventId,
    { refetchInterval: 60_000, retry: false },
  )
  const count = query.data?.unread ?? 0
  if (count === 0) return null
  return (
    <span
      className="ml-auto rounded-full bg-ember px-2 py-0.5 text-xs tabular-nums text-white"
      aria-label={t('admin.feedback.unreadBadge', { count })}
    >
      {count > 99 ? '99+' : count}
    </span>
  )
}

/**
 * En-tête d'un écran : ce qu'on regarde, et l'action principale.
 */
export function AdminHeader({
  title,
  description,
  action,
}: {
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <h1 className="font-display text-display-sm text-content-primary">{title}</h1>
        {description ? (
          <p className="mt-2 max-w-2xl text-sm text-content-secondary">{description}</p>
        ) : null}
      </div>
      {action}
    </header>
  )
}

/**
 * Barre d'enregistrement collante.
 *
 * Elle n'apparaît que s'il y a quelque chose à enregistrer. Un formulaire long
 * dont le bouton dort en bas de page fait perdre des modifications : on croit
 * avoir enregistré parce qu'on a rempli.
 */
export function SaveBar({
  dirty,
  saving,
  error,
  savedAt,
  onSave,
  onReset,
}: {
  dirty: boolean
  saving: boolean
  error: string | null
  savedAt: number | null
  onSave: () => void
  onReset: () => void
}) {
  const { t } = useTranslation()

  if (!dirty && !error && !savedAt) return null

  return (
    <div className="glass sticky bottom-4 z-20 mt-8 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-line px-5 py-3">
      <p className="text-sm">
        {error ? (
          <span className="text-state-danger">{error}</span>
        ) : dirty ? (
          <span className="text-content-secondary">{t('admin.unsaved')}</span>
        ) : (
          <span className="text-state-success">{t('admin.saved')}</span>
        )}
      </p>
      <div className="flex gap-2">
        {dirty ? (
          <Button variant="ghost" size="sm" onClick={onReset} disabled={saving}>
            {t('admin.discard')}
          </Button>
        ) : null}
        <Button size="sm" onClick={onSave} disabled={!dirty || saving}>
          {saving ? t('state.loading') : t('admin.save')}
        </Button>
      </div>
    </div>
  )
}
